import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, MessageSquare, Mail, Globe, Terminal, ChevronRight,
  CheckCircle, AlertTriangle, HelpCircle, Clock, Users, Shield,
  FileText, Zap, LayoutDashboard
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';

const channels = [
  {
    icon: MessageSquare,
    title: 'Slack',
    description: 'Users submit requests via direct message or slash command. Bot responds with clarifying questions and status updates.',
    features: ['Slash commands', 'Direct messages', 'Threaded updates', 'Button actions']
  },
  {
    icon: MessageSquare,
    title: 'Microsoft Teams',
    description: 'Native Teams integration with adaptive cards. Users interact without leaving their workspace.',
    features: ['Adaptive cards', 'Channel posts', 'Direct messages', 'Tab app']
  },
  {
    icon: Mail,
    title: 'Email',
    description: 'Dedicated intake address processes incoming requests. Automatic parsing extracts request type and details.',
    features: ['Auto-parsing', 'Attachment handling', 'Reply tracking', 'Email threading']
  },
  {
    icon: Globe,
    title: 'Web Portal',
    description: 'Self-service portal with guided request forms. Users track status and provide additional information.',
    features: ['Guided forms', 'Status tracking', 'Document upload', 'Request history']
  },
  {
    icon: Terminal,
    title: 'API',
    description: 'Programmatic request submission for integrations. Webhooks notify external systems of status changes.',
    features: ['REST API', 'Webhooks', 'Bulk operations', 'Custom fields']
  }
];

const classificationSteps = [
  {
    step: '1',
    title: 'Request Received',
    description: 'Request arrives via any supported channel. System acknowledges receipt and begins processing.'
  },
  {
    step: '2',
    title: 'Intent Classification',
    description: 'NLP model determines request type: access request, password reset, software install, etc.'
  },
  {
    step: '3',
    title: 'Information Gathering',
    description: 'System identifies missing required fields and asks follow-up questions automatically.'
  },
  {
    step: '4',
    title: 'Case Creation',
    description: 'Complete request becomes a case in Control Tower with appropriate action bundles attached.'
  },
  {
    step: '5',
    title: 'Policy Evaluation',
    description: 'Policy engine determines automation eligibility, approval requirements, and routing.'
  }
];

const requestTypes = [
  { type: 'Access Requests', examples: 'Application access, group membership, elevated privileges', route: 'Access Management' },
  { type: 'Password & MFA', examples: 'Password reset, MFA enrollment, authenticator recovery', route: 'Identity' },
  { type: 'Software', examples: 'Application install, license assignment, version upgrade', route: 'SaaS Governance' },
  { type: 'Hardware', examples: 'New device request, equipment return, peripheral request', route: 'Endpoints' },
  { type: 'Onboarding', examples: 'New hire setup, contractor access, role assignment', route: 'Identity' },
  { type: 'Offboarding', examples: 'Account deactivation, access revocation, data transfer', route: 'Identity' },
  { type: 'Security', examples: 'Suspicious activity report, phishing report, breach notification', route: 'Security Response' },
  { type: 'Other', examples: 'General questions, status inquiries, escalations', route: 'Manual Triage' }
];

const faqs = [
  {
    question: 'How does the system handle vague or incomplete requests?',
    answer: 'The classification model identifies ambiguous requests and automatically asks targeted follow-up questions. Users can respond in the same channel. Requests remain in "gathering information" state until sufficient details are collected.'
  },
  {
    question: 'What happens if a request requires manager approval?',
    answer: 'Policy engine evaluates each request against configured rules. When approval is required, the system routes to the appropriate approver with full context. Approvers can approve, reject, or request more information via their preferred channel.'
  },
  {
    question: 'How are automation boundaries enforced?',
    answer: 'Each action type has a configured automation level: observe (human executes), propose (system proposes, human approves), or auto (system executes). Policies define conditions for each level based on risk, user attributes, and organizational rules.'
  },
  {
    question: 'How is audit evidence captured for requests?',
    answer: 'Every request generates a case with complete audit trail. This includes the original request, all follow-up interactions, classification decisions, policy evaluations, approvals, and executed actions with before/after state.'
  },
  {
    question: 'Can requests be synced to our existing ITSM?',
    answer: 'Yes. Two-way sync with ServiceNow and Jira Service Management creates tickets from cases and updates ticket status as cases progress. Evidence pack links are posted to tickets for compliance documentation.'
  },
  {
    question: 'How do users check the status of their requests?',
    answer: 'Users receive proactive updates in their original channel. They can also check status by asking the bot, visiting the web portal, or using the "check status" slash command with their request reference.'
  }
];

export const ResolveRequests = () => {
  return (
    <MarketingLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <section className="py-16 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/product" className="hover:text-foreground">Product</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Resolve Requests</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
              Multi-Channel Request Intake
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl leading-relaxed">
              Meet users where they work. Accept requests via Slack, Teams, email, web portal, or API.
              Automatic classification routes requests to the right workflows.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button asChild size="default">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="default" variant="outline">
                <Link to="/control-tower">View Control Tower</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Intake Channels */}
        <section className="py-16 px-6 bg-secondary/30">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Intake Channels</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {channels.map((channel, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <channel.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground mb-1">{channel.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{channel.description}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {channel.features.map((feature, fidx) => (
                      <span
                        key={fidx}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary text-xs text-muted-foreground rounded-md"
                      >
                        <CheckCircle className="w-3 h-3" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Classification Flow */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Request Classification</h2>
            <div className="grid md:grid-cols-5 gap-4">
              {classificationSteps.map((step, idx) => (
                <div key={idx} className="relative">
                  <div className="bg-secondary/30 border border-border rounded-lg p-4 h-full">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold mb-3">
                      {step.step}
                    </div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">{step.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                  {idx < classificationSteps.length - 1 && (
                    <div className="hidden md:block absolute top-10 -right-2 w-4 text-muted-foreground">
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Request Types */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Supported Request Types</h2>
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <div className="grid grid-cols-12 bg-secondary px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-border">
                <div className="col-span-3">Request Type</div>
                <div className="col-span-6">Examples</div>
                <div className="col-span-3">Routed To</div>
              </div>
              {requestTypes.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 px-6 py-4 border-b border-border last:border-0 text-sm hover:bg-secondary/30 transition-colors">
                  <div className="col-span-3 font-medium text-foreground">{item.type}</div>
                  <div className="col-span-6 text-muted-foreground">{item.examples}</div>
                  <div className="col-span-3 text-muted-foreground">{item.route}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Integration with Control Tower */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Routed to Control Tower</h2>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Classified requests become cases in Control Tower with appropriate action bundles attached.
                  Policy engine evaluates automation level and approval requirements. Users receive updates
                  in their original channel as the case progresses.
                </p>
                <ul className="space-y-3">
                  {[
                    'Automatic case creation with full context',
                    'Action bundles selected based on request type',
                    'Policy-driven approval routing',
                    'Real-time status updates to requester'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-secondary/30 border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <LayoutDashboard className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground">Control Tower</div>
                    <div className="text-xs text-muted-foreground">Case Management</div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="bg-white border border-border rounded-md p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-foreground">Case #12847</span>
                      <span className="badge-status badge-pending">Pending Approval</span>
                    </div>
                    <div className="text-xs text-muted-foreground">Access request: Salesforce admin</div>
                  </div>
                  <div className="bg-white border border-border rounded-md p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-foreground">Case #12846</span>
                      <span className="badge-status badge-executing">Executing</span>
                    </div>
                    <div className="text-xs text-muted-foreground">New hire onboarding: Jane Smith</div>
                  </div>
                  <div className="bg-white border border-border rounded-md p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-foreground">Case #12845</span>
                      <span className="badge-status badge-completed">Completed</span>
                    </div>
                    <div className="text-xs text-muted-foreground">Password reset: user@company.com</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground mb-2 flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    {faq.question}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed pl-6">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">See Request Intake in Action</h2>
            <p className="text-muted-foreground mb-8">
              Schedule a demo to see how requests flow from Slack, Teams, or email into automated workflows.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="lg">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/product/access-management">Access Management</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default ResolveRequests;
